import $ from 'jquery';
import React from 'react';
import { string } from 'prop-types';
import createHistory from 'history/createBrowserHistory';
import { getQueryParams } from 'utils/query-param-helpers';
import { attachEnterAsClickDecorator } from 'utils/attachEnterAsClick';
import scrollTo from 'utils/scroll-to';
import Loader from '../loader';
import { initActiveFilters, manageActiveFilters, removeActiveFilterGroup, getActiveFiltersWithValues } from './utils/active-filters';
import { parseQueryString, buildQueryString } from './utils/query-string';
import FiltersLayout from './layouts/FiltersLayout';
import ListingItemsLayout from './layouts/ListingItemsLayout';
import { filterItems, paginateItems, sortItems } from './utils/item-filter';

export default class FilteredListing extends React.Component {
    constructor(props) {
        super(props);

        this.history = createHistory();
        this.state = {
            isLoading: true,
            data: {
                filterOptions: [],
                listingIntro: '',
                contentData: [],
                resources: [],
                settings: {}
            },
            APIStatus: '',
            APIMessage: '',
            activeFilters: [],
            focusedFilter: '',
            currentSort: 'default',
            currentPage: 0 // page 1 is 0 (array index value)
        };

        this.toggleFilters = this.toggleFilters.bind(this);
        this.resetFilters = this.resetFilters.bind(this);
        this.resetFilterGroup = this.resetFilterGroup.bind(this);
        this.changePage = this.changePage.bind(this);
        this.updateSort = this.updateSort.bind(this);
        this.toggleFocus = this.toggleFocus.bind(this);
    }

    componentWillMount() {
        const newState = { ...this.state };
        const pageData = this.props.rawData.trim();

        // check for JSON data on the page,
        // If it exists then we're getting data from local mock json file
        if (pageData && pageData.length) {
            // convert the page contents to JSON format & set to state
            const jsonPageData = $.parseJSON(pageData);
            this.setData(jsonPageData, newState);

            // hide the loader if we have items from mock JSON page data
            if (jsonPageData.contentData.length > 0) {
                newState.isLoading = false;
            }

            newState.activeFilters = initActiveFilters(jsonPageData.filterOptions);

            this.prePopulateFilters(newState);

            // update the app state with new preset filter values
            this.setState(...this.state, newState);

            // default sort
            this.updateSort('');
        }
    }

    componentDidMount() {
        const newState = { ...this.state };

        // If the app is still loading then get data from API
        if (this.state.isLoading) {
            // fetch data from API endpoint
            this.fetchItems(this.props.url, newState);
        }
    }

    componentWillUpdate(newProps, newState) {
        const queryParams = buildQueryString(newState);

        this.history.replace({
            pathname: window.location.pathname,
            search: queryParams
        });
    }

    setData(data, newState) {
        // set results & settings
        newState.data.contentData = data.contentData;
        newState.data.settings = data.settings;

        // set filters if they exist
        if (data.filterOptions && data.filterOptions.length) {
            newState.data.filterOptions = data.filterOptions;
        }

        // set resources if they exist
        if (data.resources && data.resources.length) {
            newState.data.resources = data.resources;
        }

        // set listingIntro if it exists
        if (data.listingIntro) {
            newState.data.listingIntro = data.listingIntro;
        }
    }

    prePopulateFilters(newState) {
        const url = getQueryParams();
        const activeFilters = this.state.activeFilters.length > 0 || initActiveFilters(this.state.data.filterOptions);
        const presetFilters = parseQueryString(url, activeFilters, this.state.currentPage);

        if (presetFilters) {
            if (JSON.stringify(presetFilters.activeFilters) !== JSON.stringify(this.state.activeFilters)) {
                newState.activeFilters = presetFilters.activeFilters;
            }

            if (presetFilters.currentSort !== newState.currentSort) {
                newState.currentSort = presetFilters.currentSort;
            }

            if (parseInt(presetFilters.currentPage, 10) !== 0) {
                newState.currentPage = parseInt(presetFilters.currentPage, 10);
            }
        }
    }

    fetchItems(url, newState) {
        $.getJSON(`/api/${url}`)
            .fail((jqxhr, textStatus, error) => {
                newState.APIStatus = 'error';
                newState.APIMessage = `${textStatus}, ${error}`;
                // update the app state with api error values
                this.setState(...this.state, newState);
            })
            .done(response => {
                this.setData(response.data, newState);

                newState.isLoading = false;

                newState.activeFilters = initActiveFilters(response.data.filterOptions);

                this.prePopulateFilters(newState);

                // update the app state with api data & new preset filter values
                this.setState(...this.state, newState);

                // default sort
                this.updateSort('');
            });
    }

    resetFilters(event) {
        event.preventDefault();
        this.setState({
            activeFilters: initActiveFilters(this.state.data.filterOptions),
            currentPage: 0
        });
    }

    resetFilterGroup(event, groupCode) {
        event.preventDefault();
        this.setState({
            activeFilters: removeActiveFilterGroup(this.state.activeFilters, groupCode),
            currentPage: 0
        });
    }

    toggleFilters(event, filter, value, forceClear = false) {
        event.preventDefault();
        this.toggleFocus(filter);
        this.setState({
            activeFilters: manageActiveFilters(this.state.activeFilters, filter.code, value, forceClear),
            currentPage: 0
        });
    }

    changePage(pageNum) {
        // Check if the current page changed, if so we need to scroll back to top of the listing section
        // We call this in a setTimeout to break it outside the execution order, so it will happen after the state update
        if (pageNum !== this.state.currentPage) {
            const app = this;
            this.scrollToAppTop();
            setTimeout(() => {
                app.setState({ currentPage: pageNum });
            }, 1000);
        }
    }

    scrollToAppTop() {
        if (this.props.listingIdentifier) {
            scrollTo(`div[data-app-id="${this.props.listingIdentifier}"] .filtered-listing-container`, 24);
        } else {
            scrollTo('.filtered-listing-container', 24);
        }
    }

    updateSort(sort) {
        this.setState({
            currentSort: sort,
            currentPage: 0
        });
    }

    toggleFocus(filter) {
        this.setState({ focusedFilter: filter ? filter.code : null });
    }

    renderAPIErrorMessage() {
        if (this.state.APIStatus !== 'error') { return null; }

        return (
            <p className="validation-msg">Oops, something went wrong: {this.state.APIMessage}</p>
        );
    }

    render() {
        const { contentData, filterOptions, resources, settings, listingIntro } = this.state.data;
        const filtersLayout = this.props.filtersLayout ? this.props.filtersLayout : 'filters-top';
        const itemContainerLayout = this.props.itemContainerLayout ? this.props.itemContainerLayout : 'four-col';
        const itemDirection = this.props.itemDirection ? this.props.itemDirection : 'vertical';
        const itemType = this.props.itemType ? this.props.itemType : 'content';
        const filterSettingsObj = {
            activeFiltersStyle: settings.activeFiltersStyle ? settings.activeFiltersStyle : 'text-list',
            showActiveFilters: settings.showActiveFilters,
            activeFiltersLabel: settings.activeFiltersLabel,
            keywordPlaceholderText: settings.keywordPlaceholderText,
            keywordSearchableFields: settings.keywordSearchableFields
        };

        if (this.state.isLoading) {
            return (
                <div>
                    <Loader />
                    {this.renderAPIErrorMessage}
                </div>
            );
        }

        const activeFilters = getActiveFiltersWithValues(this.state.activeFilters);
        const filteredItems = filterItems(activeFilters, contentData, filterSettingsObj.keywordSearchableFields);
        const sortedItems = sortItems(filteredItems, this.state.currentSort);
        const pagedItems = paginateItems(sortedItems, this.state.currentPage, settings.itemsPerPage);

        return (
            <div className={`c-filtered-listing ${filtersLayout}`}>
                <div className="filtered-listing-container">
                    <div className="filtered-listing">
                        <FiltersLayout
                            filters={filterOptions}
                            activeFilters={activeFilters}
                            filterSettings={filterSettingsObj}
                            toggleFilters={attachEnterAsClickDecorator(this.toggleFilters)}
                            resetFilters={this.resetFilters}
                            resetFilterGroup={this.resetFilterGroup}
                            focusedFilter={this.state.focusedFilter}
                            toggleFocus={this.toggleFocus}
                        />

                        <ListingItemsLayout
                            itemWrapperModifier={itemContainerLayout}
                            itemType={itemType}
                            itemDirection={itemDirection}
                            listingItems={pagedItems}
                            resources={resources}
                            listingIntro={listingIntro}

                            showResultCount={settings.showResultCount}
                            itemCount={filteredItems.length}
                            sortBy={settings.sortBy}
                            currentSort={this.state.currentSort}
                            updateSort={this.updateSort}

                            totalPages={Math.ceil(filteredItems.length / settings.itemsPerPage)}
                            itemsPerPage={settings.itemsPerPage}
                            currentPage={this.state.currentPage}
                            changePage={this.changePage}
                            resetFilters={this.resetFilters}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

FilteredListing.propTypes = {
    /* eslint-disable react/no-typos */
    // The disable is needed until https://github.com/yannickcr/eslint-plugin-react/issues/1389 is resolved
    url: string.isRequired,
    rawData: string,
    itemType: string,
    itemDirection: string,
    itemContainerLayout: string,
    listingIdentifier: string,
    filtersLayout: string
    /* eslint-enable react/no-typos */
};

FilteredListing.defaultProps = {
    rawData: '',
    itemType: '',
    itemDirection: '',
    itemContainerLayout: '',
    listingIdentifier: '',
    filtersLayout: ''
};



// WEBPACK FOOTER //
// ./src/components/filtered-listing/index.jsx